/**
 * Shared per-type calculation form pieces. Used by both the Calculations
 * tab's inline grid and the Preview tab's edit dialog so the UX matches.
 */
import {
  Alert, Autocomplete, Box, Checkbox, FormControlLabel, Grid,
  IconButton, Link, ListSubheader, Paper, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Link as RouterLink } from 'react-router-dom';
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit';
import { useEffect, useLayoutEffect, useRef, useState, type Ref } from 'react';
import FunctionPicker, { type FormulaDialect } from './FunctionPicker';
import LookupInlineEditor, { type LookupInlineEditorHandle } from './LookupInlineEditor';
import type { CalcType, LookupDefinition, LookupSummary } from './types';

export const CALC_LABELS: Record<CalcType, string> = {
  E: 'Formula',
  C: 'If / Then / Else',
  L: 'Lookup',
  S: 'Custom SQL',
};

// ---- parse/build helpers (kept here so dialog can round-trip too) ----------

export function parseConditional(expr: string) {
  const parts = (expr || '').split(':');
  return {
    condition: parts[0] || '',
    trueExpr: parts[1] || '',
    falseExpr: parts[2] || '',
  };
}
export function buildConditional(c: string, t: string, f: string) {
  return `${c}:${t}:${f}`;
}

export interface LookupExprParts {
  lookupTable: string;
  isDynamic: boolean;
  keyColumn: string;
  dateColumn: string;
}

export function parseLookup(expr: string): LookupExprParts {
  const parts = (expr || '').split(',').map((p) => p.trim());
  let table = parts[0] || '';
  const dyn = !(table.startsWith("'") && table.endsWith("'"));
  if (!dyn) table = table.slice(1, -1);
  return {
    lookupTable: table,
    isDynamic: dyn,
    keyColumn: (parts[1] || '').toLowerCase(),
    dateColumn: parts[2] ? parts[2].toLowerCase() : '',
  };
}
export function buildLookup(table: string, isDynamic: boolean, keyCol: string, dateCol?: string) {
  const head = isDynamic ? table : `'${table}'`;
  return [head, keyCol, dateCol].filter(Boolean).join(', ');
}

// ---- shared field: monospace input + click/drag-to-insert columns ----------

/** A single column the user can click (insert at caret) or drag into the
 *  formula. Drag carries the bare name as ``text/plain`` so the native
 *  text-input drop handler inserts it at the exact drop position. */
function ColumnToken({
  name, onInsert, block = false,
}: { name: string; onInsert: (n: string) => void; block?: boolean }) {
  return (
    <Box
      role="button"
      draggable
      onClick={() => onInsert(name)}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', name);
        e.dataTransfer.effectAllowed = 'copyMove';
      }}
      title="Click to insert, or drag into the formula"
      sx={{
        cursor: 'grab',
        userSelect: 'none',
        fontSize: 13,
        fontFamily: 'monospace',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0.75, py: 0.25,
        ...(block ? { width: '100%', mb: 0.5 } : { mr: 0.5, mb: 0.5 }),
        bgcolor: 'grey.100',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 1,
        '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {block && <DragIndicatorIcon sx={{ fontSize: 15, opacity: 0.5, flexShrink: 0 }} />}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </Box>
    </Box>
  );
}

export function FormulaField({
  value, onChange, colOptions, placeholder, multiline, dialect, functions = true,
  columnLayout = 'chips',
}: {
  value: string;
  onChange: (v: string) => void;
  colOptions: string[];
  placeholder?: string;
  multiline?: boolean;
  /** Which engine the field targets — controls which functions are offered. */
  dialect?: FormulaDialect;
  /** Show the fx function picker (off for SQL, which isn't a pandas formula). */
  functions?: boolean;
  /** 'chips' = wrap columns below the input; 'panel' = scrollable side panel. */
  columnLayout?: 'chips' | 'panel';
}) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // Highlights the input while a column is dragged over it.
  const [dragOver, setDragOver] = useState(false);
  // Caret/selection captured on blur — clicking the fx button or a column chip
  // blurs the field, so this is the position to insert at. Null = never focused.
  const lastSel = useRef<{ start: number; end: number } | null>(null);
  // After an insert changes `value`, restore focus and place the caret.
  const [pendingCaret, setPendingCaret] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (pendingCaret != null && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      el.setSelectionRange(pendingCaret, pendingCaret);
      lastSel.current = { start: pendingCaret, end: pendingCaret };
      setPendingCaret(null);
    }
  }, [pendingCaret, value]);

  const captureSel = () => {
    const el = inputRef.current;
    if (el && el.selectionStart != null) {
      lastSel.current = { start: el.selectionStart, end: el.selectionEnd ?? el.selectionStart };
    }
  };

  /**
   * Insert a template at the caret. ``$0`` marks where the current selection
   * goes (enabling nesting): with text selected, the selection is wrapped as
   * the argument; with no selection the caret lands at ``$0`` so the user can
   * type it. Templates without ``$0`` are inserted verbatim (e.g. column names).
   */
  const insertSnippet = (template: string) => {
    const el = inputRef.current;
    // Prefer the live caret if the field still has focus; else the last
    // captured position; else append at the end.
    let start = value.length;
    let end = value.length;
    if (el && document.activeElement === el && el.selectionStart != null) {
      start = el.selectionStart;
      end = el.selectionEnd ?? start;
    } else if (lastSel.current) {
      start = Math.min(lastSel.current.start, value.length);
      end = Math.min(lastSel.current.end, value.length);
    }

    const selected = value.slice(start, end);
    const markerIdx = template.indexOf('$0');
    const filled = markerIdx >= 0
      ? template.slice(0, markerIdx) + selected + template.slice(markerIdx + 2)
      : template;
    const newValue = value.slice(0, start) + filled + value.slice(end);
    onChange(newValue);

    // No selection -> land the caret at the marker so they type the arg.
    // Selection wrapped -> land after the whole inserted block.
    const caret = (markerIdx >= 0 && !selected)
      ? start + markerIdx
      : start + filled.length;
    setPendingCaret(caret);
  };

  // A column dropped onto a native text input is inserted by the browser at
  // the exact drop position; we only need to sync React's state afterwards.
  // Dragging a selection within the field (a native text "move") is captured
  // the same way. preventDefault is intentionally NOT called so the native
  // insertion runs.
  const handleDrop = () => {
    setDragOver(false);
    const el = inputRef.current;
    if (el) setTimeout(() => { if (el.value !== value) onChange(el.value); }, 0);
  };

  const inputBlock = (
    <Stack
      direction="row" spacing={1} alignItems="flex-start"
      sx={[
        { flexGrow: 1, minWidth: 0, borderRadius: 1 },
        dragOver && ((t) => ({ boxShadow: `inset 0 0 0 2px ${t.palette.primary.main}` })),
      ]}
      onDragOver={() => { if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => {
        // Only clear when the pointer truly leaves the block — not when it
        // crosses between the input's inner elements (dragleave bubbles).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
      }}
      onDrop={handleDrop}
    >
      <TextField
        size="small" fullWidth value={value}
        inputRef={inputRef}
        onChange={(e) => onChange(e.target.value)}
        onSelect={captureSel}
        onBlur={captureSel}
        placeholder={placeholder}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        sx={{
          '& input, & textarea': { fontFamily: 'monospace', fontSize: 14 },
        }}
      />
      {functions && <FunctionPicker onInsert={insertSnippet} dialect={dialect} />}
    </Stack>
  );

  if (columnLayout === 'panel' && colOptions.length > 0) {
    return (
      <Stack direction="row" spacing={1} alignItems="stretch">
        {inputBlock}
        <Paper
          variant="outlined"
          sx={{ width: 190, flexShrink: 0, maxHeight: 260, overflowY: 'auto', p: 1, bgcolor: 'grey.50' }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.75 }}>
            Columns · drag in
          </Typography>
          <Stack spacing={0}>
            {colOptions.map((c) => (
              <ColumnToken key={c} name={c} onInsert={insertSnippet} block />
            ))}
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Box>
      {inputBlock}
      {colOptions.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
          {colOptions.slice(0, 20).map((c) => (
            <ColumnToken key={c} name={c} onInsert={insertSnippet} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

// ---- per-type forms --------------------------------------------------------

export function ExpressionForm({
  value, onChange, colOptions,
}: { value: string; onChange: (v: string) => void; colOptions: string[] }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        Formula evaluated on each row. Refer to other columns by name — click a
        column or drag it into the formula. Functions nest freely — e.g.{' '}
        <code>round(if(total_sales &gt; 150000, total_sales * 0.02, 0), 2)</code>.
      </Typography>
      <FormulaField
        value={value}
        onChange={onChange}
        colOptions={colOptions}
        dialect="formula"
        columnLayout="panel"
        multiline
        placeholder="e.g. round(if(total_sales > 150000, total_sales * 0.02, 0), 2)"
      />
    </Box>
  );
}

export function ConditionalForm({
  condition, trueExpr, falseExpr, colOptions, onChange,
}: {
  condition: string; trueExpr: string; falseExpr: string;
  colOptions: string[];
  onChange: (c: string, t: string, f: string) => void;
}) {
  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>If…</Typography>
        <FormulaField value={condition}
          onChange={(v) => onChange(v, trueExpr, falseExpr)}
          colOptions={colOptions}
          placeholder="total_sales > 200000" />
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Then…</Typography>
        <FormulaField value={trueExpr}
          onChange={(v) => onChange(condition, v, falseExpr)}
          colOptions={colOptions}
          placeholder="'top-performer'" />
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Else…</Typography>
        <FormulaField value={falseExpr}
          onChange={(v) => onChange(condition, trueExpr, v)}
          colOptions={colOptions}
          placeholder="'standard'" />
      </Grid>
    </Grid>
  );
}

/** One unified Autocomplete option type:
 *  - saved  : an existing saved lookup table
 *  - column : a source-SQL column whose row value names the table per-row
 *  - create : sentinel for "create a new lookup with the typed name"
 */
type LookupOption =
  | { kind: 'saved';  value: string }
  | { kind: 'column'; value: string }
  | { kind: 'create'; value: string };

export function LookupForm({
  value, colOptions, lookups, onChange, onLookupSaved, inlineEditorRef,
}: {
  value: LookupExprParts;
  colOptions: string[];
  lookups: LookupSummary[];
  onChange: (v: LookupExprParts) => void;
  /** Fires after the user saves a new/edited lookup inline. */
  onLookupSaved?: (def: LookupDefinition) => void;
  /** Ref forwarded to the inline editor so the parent can flush pending
   *  lookup edits before its own save action. */
  inlineEditorRef?: Ref<LookupInlineEditorHandle>;
}) {
  // Build the grouped option list. Saved lookups come first (most common
  // intent), then source columns (per-row table name), then a synthetic
  // "create new" entry that mirrors whatever the user has typed.
  const savedNames = new Set(lookups.map((l) => l.lookup_table.toLowerCase()));
  const columnSet = new Set(colOptions.map((c) => c.toLowerCase()));
  const options: LookupOption[] = [
    ...lookups.map((l) => ({ kind: 'saved' as const, value: l.lookup_table })),
    ...colOptions.map((c) => ({ kind: 'column' as const, value: c })),
  ];
  const typed = (value.lookupTable || '').trim();
  const typedLower = typed.toLowerCase();
  if (typed && !savedNames.has(typedLower) && !columnSet.has(typedLower)) {
    options.push({ kind: 'create', value: typed });
  }

  // The currently-selected option (used so the Autocomplete renders the
  // right group/badge in the value chip). Falls back to a synthetic option
  // matching the current mode.
  const currentOption: LookupOption | null = !typed ? null : value.isDynamic
    ? { kind: 'column', value: typed }
    : (savedNames.has(typedLower)
        ? { kind: 'saved',  value: typed }
        : { kind: 'create', value: typed });

  // The "As of" field is hidden until the user explicitly opts in. Local
  // toggle state lets us show the empty Autocomplete after the checkbox
  // turns on but before a column is picked. Auto-syncs to ``true`` if the
  // calc was loaded with a date column already set.
  const [showDate, setShowDate] = useState<boolean>(Boolean(value.dateColumn));
  useEffect(() => {
    if (value.dateColumn && !showDate) setShowDate(true);
  }, [value.dateColumn, showDate]);

  return (
    <Box>
      <Grid container spacing={1.5} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Autocomplete<LookupOption, false, false, true>
              freeSolo
              disablePortal
              options={options}
              value={currentOption}
              inputValue={typed}
              groupBy={(o) => (typeof o === 'string' ? '' : (
                o.kind === 'saved'  ? 'Saved lookup tables' :
                o.kind === 'column' ? 'Use a row column (per-row name)' :
                                      'Other'
              ))}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.value)}
              isOptionEqualToValue={(a, b) =>
                typeof a !== 'string' && typeof b !== 'string'
                && a.kind === b.kind && a.value.toLowerCase() === b.value.toLowerCase()
              }
              renderGroup={(params) => (
                <li key={params.key}>
                  <ListSubheader component="div" sx={{
                    bgcolor: 'grey.100', lineHeight: '28px', fontSize: 13,
                    color: 'text.secondary', fontWeight: 600,
                  }}>
                    {params.group}
                  </ListSubheader>
                  <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                </li>
              )}
              renderOption={(props, o) => {
                if (typeof o === 'string') {
                  return <li {...props}>{o}</li>;
                }
                const icon =
                  o.kind === 'saved'  ? <TableChartIcon fontSize="small" sx={{ color: 'primary.main' }} /> :
                  o.kind === 'column' ? <ViewColumnIcon  fontSize="small" sx={{ color: 'secondary.main' }} /> :
                                        <AddIcon         fontSize="small" sx={{ color: 'success.main' }} />;
                const label =
                  o.kind === 'create' ? `Create new lookup “${o.value}”` : o.value;
                return (
                  <li {...props}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {icon}
                      <Typography variant="body2">{label}</Typography>
                    </Stack>
                  </li>
                );
              }}
              onInputChange={(_, v) => onChange({ ...value, lookupTable: v || '' })}
              onChange={(_, picked) => {
                if (!picked) {
                  onChange({ ...value, lookupTable: '', isDynamic: false });
                  return;
                }
                if (typeof picked === 'string') {
                  // Free-typed string: classify by whether it matches a known column.
                  const lower = picked.toLowerCase();
                  onChange({
                    ...value,
                    lookupTable: picked,
                    isDynamic: columnSet.has(lower) && !savedNames.has(lower),
                  });
                  return;
                }
                onChange({
                  ...value,
                  lookupTable: picked.value,
                  isDynamic: picked.kind === 'column',
                });
              }}
              sx={{ flexGrow: 1 }}
              renderInput={(p) => (
                <TextField {...p} size="small" label="Lookup table"
                  helperText={
                    currentOption?.kind === 'column'
                      ? `The lookup table name comes from each row's "${currentOption.value}" column.`
                      : currentOption?.kind === 'create'
                      ? `Will create a new lookup called "${currentOption.value}".`
                      : 'Pick a saved lookup, a row column (per-row name), or type a new name.'
                  }
                />
              )}
            />
            <Tooltip title="Manage all lookups in a new tab">
              <IconButton size="small" component={RouterLink} to="/lookups" target="_blank">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            freeSolo
            disablePortal
            options={colOptions}
            value={value.keyColumn}
            onInputChange={(_, v) => onChange({ ...value, keyColumn: (v || '').toLowerCase() })}
            renderInput={(p) => (
              <TextField {...p} size="small" label="Look up by"
                helperText="The row column whose value is matched against the lookup."
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={showDate}
                onChange={(e) => {
                  setShowDate(e.target.checked);
                  // Turning the toggle off also clears any picked column so
                  // the persisted expression stops referencing an as-of date.
                  if (!e.target.checked) onChange({ ...value, dateColumn: '' });
                }}
              />
            }
            label={
              <Typography variant="body2">Filter the lookup by an as-of date</Typography>
            }
          />
          {showDate && (
            <Box sx={{ mt: 1, maxWidth: 360 }}>
              <Autocomplete
                freeSolo
                disablePortal
                options={colOptions}
                value={value.dateColumn}
                onInputChange={(_, v) => onChange({ ...value, dateColumn: (v || '').toLowerCase() })}
                renderInput={(p) => (
                  <TextField {...p} size="small" label="As of (row column)"
                    helperText="Only rows where this date falls between Effective from and Valid until are considered."
                  />
                )}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Inline editor for the picked or to-be-created lookup. When the user
          chose a row column instead of a lookup name, show a soft signpost
          explaining where the per-row data lives. */}
      {value.isDynamic && value.lookupTable ? (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mt: 1.5 }}
          icon={<ViewColumnIcon fontSize="small" />}
        >
          The lookup table name will be read from each row's
          <strong> {value.lookupTable} </strong>
          column. Manage the actual lookup contents in the{' '}
          <Link component={RouterLink} to="/lookups" target="_blank" rel="noopener">
            Lookups tab
          </Link>.
        </Alert>
      ) : (!value.isDynamic && value.lookupTable && (
        <LookupInlineEditor
          ref={inlineEditorRef}
          lookupName={value.lookupTable}
          showDateFilter={showDate}
          onSaved={onLookupSaved}
        />
      ))}
    </Box>
  );
}

export function SqlForm({
  value, onChange, colOptions,
}: { value: string; onChange: (v: string) => void; colOptions: string[] }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        SQL run against the source DB once per row. Use <code>{'{column_name}'}</code> to splice this row's value.
        The first cell of the first row of the result becomes the value.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <SqlBuilderButton value={value} onChange={onChange} mode="select" title="Per-row SQL" />
      </Box>
      <FormulaField
        value={value}
        onChange={onChange}
        colOptions={colOptions}
        functions={false}
        placeholder="SELECT label FROM tax_codes WHERE country = '{country_code}'"
        multiline
      />
    </Box>
  );
}
